import { cn } from '@/lib/utils';
import { MapCircle } from '@/components/shared';
import type { PlacedArtifact } from '@/config/objects/placements';
import type { ArtifactCategory } from '@/config/objects/artifacts';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';
import { useEffect, useId, useMemo } from 'react';

interface ArtifactOverviewMapProps {
  artifacts: PlacedArtifact[];
  googleMapsApiKey?: string;
  className?: string;
}

const CATEGORY_COLORS: Record<ArtifactCategory, string> = {
  offensive: '#EF4444',
  defensive: '#3B82F6',
  wildcard: '#A855F7',
};

const INACTIVE_COLOR = '#9CA3AF';

const CATEGORY_LABELS: Record<ArtifactCategory, string> = {
  offensive: 'Offensive',
  defensive: 'Defensive',
  wildcard: 'Wildcard',
};

function ArtifactPin({ color }: { color: string }) {
  return (
    <div
      className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-white shadow-md"
      style={{ backgroundColor: color }}
    >
      <div className="w-2.5 h-2.5 rounded-full bg-white/80" />
    </div>
  );
}

function MapBoundsController({ artifacts }: { artifacts: PlacedArtifact[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || artifacts.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    artifacts.forEach((a) => {
      bounds.extend({ lat: a.latitude, lng: a.longitude });
      // Include detection radius in bounds
      const radiusLat = a.detectionRadius / 111320;
      const radiusLng = a.detectionRadius / (111320 * Math.cos((a.latitude * Math.PI) / 180));
      bounds.extend({ lat: a.latitude + radiusLat, lng: a.longitude + radiusLng });
      bounds.extend({ lat: a.latitude - radiusLat, lng: a.longitude - radiusLng });
    });

    map.fitBounds(bounds, { top: 40, right: 40, bottom: 60, left: 40 });
  }, [map, artifacts]);

  return null;
}

function ArtifactOverviewMapInner({
  artifacts,
  className,
}: Omit<ArtifactOverviewMapProps, 'googleMapsApiKey'>) {
  const mapId = useId();

  const defaultCenter = useMemo(() => {
    if (artifacts.length === 0) return { lat: 39.8283, lng: -98.5795 };
    const avgLat = artifacts.reduce((sum, a) => sum + a.latitude, 0) / artifacts.length;
    const avgLng = artifacts.reduce((sum, a) => sum + a.longitude, 0) / artifacts.length;
    return { lat: avgLat, lng: avgLng };
  }, [artifacts]);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <Map
        mapId={mapId}
        defaultCenter={defaultCenter}
        defaultZoom={13}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        className="w-full h-full"
      >
        <MapBoundsController artifacts={artifacts} />

        {artifacts.map((artifact) => {
          const category = artifact.artifactCategory as ArtifactCategory;
          const color = artifact.isActive
            ? (CATEGORY_COLORS[category] || CATEGORY_COLORS.wildcard)
            : INACTIVE_COLOR;

          return (
            <AdvancedMarker
              key={artifact.id}
              position={{ lat: artifact.latitude, lng: artifact.longitude }}
              title={artifact.name}
            >
              <ArtifactPin color={color} />
            </AdvancedMarker>
          );
        })}

        {artifacts.map((artifact) => {
          const category = artifact.artifactCategory as ArtifactCategory;
          const color = artifact.isActive
            ? (CATEGORY_COLORS[category] || CATEGORY_COLORS.wildcard)
            : INACTIVE_COLOR;

          return (
            <MapCircle
              key={`circle-${artifact.id}`}
              center={{ lat: artifact.latitude, lng: artifact.longitude }}
              radius={artifact.detectionRadius}
              fillColor={color}
              fillOpacity={artifact.isActive ? 0.12 : 0.06}
              strokeColor={color}
              strokeOpacity={artifact.isActive ? 0.5 : 0.25}
              strokeWeight={1.5}
            />
          );
        })}
      </Map>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border text-xs space-y-1">
        {(Object.entries(CATEGORY_LABELS) as [ArtifactCategory, string][]).map(([cat, label]) => (
          <div key={cat} className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: CATEGORY_COLORS[cat] }}
            />
            <span className="text-foreground">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 border-t pt-1 mt-1">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: INACTIVE_COLOR }}
          />
          <span className="text-muted-foreground">Inactive</span>
        </div>
      </div>
    </div>
  );
}

export function ArtifactOverviewMap({
  artifacts,
  googleMapsApiKey,
  className,
}: ArtifactOverviewMapProps) {
  if (artifacts.length === 0) return null;

  if (!googleMapsApiKey) {
    return (
      <div
        className={cn(
          'rounded-b-2xl bg-muted/50 flex flex-col items-center justify-center text-muted-foreground h-64',
          className,
        )}
      >
        <MapPin className="h-6 w-6 mb-1" />
        <span className="text-sm">Map unavailable — no API key configured</span>
      </div>
    );
  }

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <ArtifactOverviewMapInner artifacts={artifacts} className={className} />
    </APIProvider>
  );
}
